/*
 *  Splits a PDF File into N-page chunks for chunked OCR upload.
 *  Non-PDF files (image/*) pass through unchanged.
 *
 *  Why chunk:
 *    - Avoids the 200 MB per-request limit on huge passport PDFs.
 *    - Lets the upload progress advance per chunk instead of stalling at
 *      99% while the server OCRs hundreds of pages in one go.
 *    - A failed chunk only loses N pages, not the whole batch.
 */

import { PDFDocument } from 'pdf-lib';

export const DEFAULT_PAGES_PER_CHUNK = 20;

export async function splitPdfIntoChunks(
  file: File,
  pagesPerChunk: number = DEFAULT_PAGES_PER_CHUNK,
): Promise<File[]> {
  if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
    return [file];
  }

  const buf = await file.arrayBuffer();
  const src = await PDFDocument.load(buf, { ignoreEncryption: true });
  const totalPages = src.getPageCount();
  if (totalPages <= pagesPerChunk) return [file];

  const baseName = file.name.replace(/\.pdf$/i, '');
  const chunks: File[] = [];

  for (let start = 0; start < totalPages; start += pagesPerChunk) {
    const dst = await PDFDocument.create();
    const end = Math.min(start + pagesPerChunk, totalPages);
    const indices = Array.from({ length: end - start }, (_, i) => start + i);
    const copied = await dst.copyPages(src, indices);
    copied.forEach((p) => dst.addPage(p));
    const bytes = await dst.save();
    // pdf-lib returns Uint8Array<ArrayBufferLike>; File ctor wants a BlobPart.
    // Wrap in Blob to satisfy the type checker across TS lib versions.
    const blob = new Blob([bytes as unknown as BlobPart], { type: 'application/pdf' });
    chunks.push(new File([blob], `${baseName}_part${chunks.length + 1}.pdf`, {
      type: 'application/pdf',
    }));
  }

  return chunks;
}

export async function expandFilesIntoChunks(
  files: File[],
  pagesPerChunk: number = DEFAULT_PAGES_PER_CHUNK,
): Promise<File[]> {
  const out: File[] = [];
  for (const f of files) {
    const parts = await splitPdfIntoChunks(f, pagesPerChunk);
    out.push(...parts);
  }
  return out;
}
